import React from "react";
import { Modal } from "react-bootstrap";
import Resources from "../../../types/Resources";
import ChecklistRadioOptions from "../../Common/ChecklistRadioOptions";
import AsbestosChecklistFormState from "../../../types/forms/Permits/Conditions/AsbestosChecklistFormState";
import { ASBESTOS_CHECKLIST_VERSION_2026_02 } from "../../../types/permits/conditions/AsbestosChecklist";

type AsbestosChecklistModalProps = {
    checklistVersion: number;
    resources: Resources;
    formState: AsbestosChecklistFormState;
    onChange: (fn: (prevValue: AsbestosChecklistFormState) => AsbestosChecklistFormState) => void;
    show: boolean;
    onHide: () => void;
    canManageContent: boolean;
};

function AsbestosChecklistModal({
    checklistVersion,
    resources,
    onChange,
    formState,
    show,
    onHide,
    canManageContent
}: AsbestosChecklistModalProps) {
    function changeHandler(key: keyof AsbestosChecklistFormState, value: any) {
        onChange((prevValue) => {
            const updatedFormState: AsbestosChecklistFormState = {
                ...prevValue,
                [key]: { ...prevValue[key], value }
            };

            return updatedFormState;
        });
    }

    return (
        <Modal show={show} onHide={onHide} size="xl">
            <Modal.Header closeButton>
                <h1 className="h5">{resources.get("CheckListAsbestos")}</h1>
            </Modal.Header>
            <Modal.Body>
                <ul className="list-unstyled">
                    <li className="m-2">
                        <ChecklistRadioOptions
                            id="leader-and-personal-have-correct-education"
                            labelText={resources.get("LeaderAndPersonalHaveCorrectEducation")}
                            onChange={(updatedValue) =>
                                changeHandler("leaderAndPersonalHaveCorrectEducation", updatedValue)
                            }
                            resources={resources}
                            value={formState.leaderAndPersonalHaveCorrectEducation.value}
                            readOnly={!canManageContent}
                        />
                    </li>
                    <li className="m-2">
                        <ChecklistRadioOptions
                            id="is-total-time-for-sanitation-more-than-one-hour"
                            labelText={resources.get("IsTotalTimeForSanitationMoreThanOneHour")}
                            onChange={(updatedValue) =>
                                changeHandler("isTotalTimeForSanitationMoreThanOneHour", updatedValue)
                            }
                            resources={resources}
                            value={formState.isTotalTimeForSanitationMoreThanOneHour.value}
                            readOnly={!canManageContent}
                        />
                    </li>
                    {checklistVersion !== 0 ? (
                        <li className="m-2">
                            <ChecklistRadioOptions
                                id="has-approval-for-this-project"
                                labelText={resources.get("HasApprovalForThisProject")}
                                onChange={(updatedValue) =>
                                    changeHandler("hasApprovalForThisProject", updatedValue)
                                }
                                resources={resources}
                                value={formState.hasApprovalForThisProject.value}
                                readOnly={!canManageContent}
                            />
                        </li>
                    ) : (
                        <li className="m-2">
                            <ChecklistRadioOptions
                                id="has-approval-from-environmental-agency"
                                labelText={resources.get("HasApprovalFromEnvironmentalAgency")}
                                onChange={(updatedValue) =>
                                    changeHandler("hasApprovalFromEnvironmentalAgency", updatedValue)
                                }
                                resources={resources}
                                value={formState.hasApprovalFromEnvironmentalAgency.value}
                                readOnly={!canManageContent}
                            />
                        </li>
                    )}
                    <li className="m-2 d-flex align-items-start justify-content-between">
                        <div>
                            {resources.get("HasCorrectDocuments0")}
                            <ol>
                                <li>
                                    <small>{resources.get("HasCorrectDocuments1")}</small>
                                </li>
                                <li>
                                    <small>{resources.get("HasCorrectDocuments2")}</small>
                                </li>
                                <li>
                                    <small>{resources.get("HasCorrectDocuments3")}</small>
                                </li>
                                <li>
                                    <small>{resources.get("HasCorrectDocuments4")}</small>
                                </li>
                            </ol>
                        </div>
                        <ChecklistRadioOptions
                            id="has-correct-documents"
                            labelText=""
                            onChange={(updatedValue) => changeHandler("hasCorrectDocuments", updatedValue)}
                            resources={resources}
                            value={formState.hasCorrectDocuments.value}
                            readOnly={!canManageContent}
                        />
                    </li>
                    <li className="m-2">
                        <ChecklistRadioOptions
                            id="external-personal-is-contracted"
                            labelText={resources.get("ExternalPersonalIsContracted")}
                            onChange={(updatedValue) => changeHandler("externalPersonalIsContracted", updatedValue)}
                            resources={resources}
                            value={formState.externalPersonalIsContracted.value}
                            readOnly={!canManageContent}
                        />
                    </li>
                    <li className="m-2">
                        <ChecklistRadioOptions
                            id="is-whole-mask-with-compressed-air-used"
                            labelText={resources.get("IsWholeMaskWithCompressedAirUsed")}
                            onChange={(updatedValue) => changeHandler("isWholeMaskWithCompressedAirUsed", updatedValue)}
                            resources={resources}
                            value={formState.isWholeMaskWithCompressedAirUsed.value}
                            readOnly={!canManageContent}
                        />
                    </li>
                    <li className="m-2">
                        <ChecklistRadioOptions
                            id="are-masks-fitted-for-resp-person"
                            labelText={resources.get("AreMasksFittedForRespPerson")}
                            onChange={(updatedValue) => changeHandler("areMasksFittedForRespPerson", updatedValue)}
                            resources={resources}
                            value={formState.areMasksFittedForRespPerson.value}
                            readOnly={!canManageContent}
                        />
                    </li>
                    <li className="m-2">
                        <ChecklistRadioOptions
                            id="masks-are-according-to-rules"
                            labelText={resources.get("MasksAreStoredAccordingAccordingToRules")}
                            onChange={(updatedValue) =>
                                changeHandler("masksAreStoredAccordingAccordingToRules", updatedValue)
                            }
                            resources={resources}
                            value={formState.masksAreStoredAccordingAccordingToRules.value}
                            readOnly={!canManageContent}
                        />
                    </li>
                    <li className="m-2">
                        <ChecklistRadioOptions
                            id="are-own-machines-used-for-sanitation"
                            labelText={resources.get("AreOwnMachinesUsedForSanitation")}
                            onChange={(updatedValue) => changeHandler("areOwnMachinesUsedForSanitation", updatedValue)}
                            resources={resources}
                            value={formState.areOwnMachinesUsedForSanitation.value}
                            readOnly={!canManageContent}
                        />
                    </li>
                    <li className="m-2">
                        <ChecklistRadioOptions
                            id="is-sanitized-from-asbestos-dust"
                            labelText={resources.get("IsSanitisedFromAsbestosDust")}
                            onChange={(updatedValue) => changeHandler("isSanitisedFromAsbestosDust", updatedValue)}
                            resources={resources}
                            value={formState.isSanitisedFromAsbestosDust.value}
                            readOnly={!canManageContent}
                        />
                    </li>
                    <li className="m-2">
                        <ChecklistRadioOptions
                            id="has-sign-for-asbestos"
                            labelText={resources.get("HasSignForAsbestos")}
                            onChange={(updatedValue) => changeHandler("hasSignForAsbestos", updatedValue)}
                            resources={resources}
                            value={formState.hasSignForAsbestos.value}
                            readOnly={!canManageContent}
                        />
                    </li>
                </ul>
            </Modal.Body>
        </Modal>
    );
}

export default AsbestosChecklistModal;
